/** The email notification categories a parent can turn on/off (#165). */
export type NotifyCategory = 'focus' | 'offline' | 'done' | 'help' | 'chat' | 'whiteboard' | 'support' | 'behind' | 'deadlines' | 'evaluator';
export type NotifyPrefs = Record<NotifyCategory, boolean>;

export const NOTIFY_LABELS: Record<NotifyCategory, string> = {
  focus: 'Focus alerts (blocked sites, leaving the learning sites)',
  offline: 'The app goes offline during school time',
  done: 'Your child says they’re done for the day',
  help: 'Your child asks for help',
  chat: 'Chat messages from your child',
  whiteboard: 'Your child opens the whiteboard',
  support: 'Replies to your Help & Feedback requests',
  behind: 'Weekly note when a child falls behind your state’s days or hours',
  deadlines: 'Reminders ahead of your state’s filing deadlines',
  evaluator: 'An evaluator opens a school year you shared',
};

/** Stickers a student can put on an assignment they turn in (#188) — must match the server's list. */
export const STICKERS = ['⭐', '🌟', '🎉', '🚀', '🏆', '💯', '🌈', '🦄', '🦖', '🐱', '🎨', '🍕', '🐶', '🦋', '🐼', '🌻', '🍩', '⚽', '🎸', '🧠', '👑', '🔥'] as const;

/** Categories that can show an on-device (browser) notification — all of them (#217). */
export const BROWSER_CATEGORIES: readonly NotifyCategory[] = ['focus', 'offline', 'done', 'help', 'chat', 'whiteboard', 'support', 'behind', 'deadlines', 'evaluator'];

/** Hours by subject (#607). */
export interface SubjectPart {
  subject: string;
  hours: number;
  /** Split from the day's planner by itself (#691), not saved by a parent. */
  auto?: boolean;
}
export interface SubjectTotals {
  days: Record<string, SubjectPart[]>;
  subjects: { subject: string; hours: number; core: boolean }[];
  coreHours: number;
  unsplitHours: number;
  recordedHours: number;
  coreSubjects: string[];
}

/** A photo or PDF of a child's work (#603), on an assignment or a day's journal entry. */
export interface WorkSample {
  id: string;
  plannerItemId: string | null;
  /** A grade-book-only assignment's work (#637). */
  assignmentId?: string | null;
  journalDate: string | null;
  date: string;
  name: string;
  mime: string;
  size: number;
  caption: string;
  inPortfolio: boolean;
  addedBy: 'parent' | 'student';
  createdAt: number;
}

export type PortfolioSection = 'summary' | 'attendance' | 'lessons' | 'grades' | 'reading' | 'journal' | 'tests' | 'trips' | 'samples';

/** Closing out a school year (#623): the current year, whether it's ended or closed, and the closed ones. */
export interface SchoolYearState {
  enabled: boolean;
  year: { start: string; end: string; label: string } | null;
  ended: boolean;
  closed: boolean;
  next: { start: string; end: string } | null;
  transcripts: boolean;
  children: { id: string; name: string; grade: string; nextGrade: string; days: number; hours: number; samples: number; transcript: boolean }[];
  closes: { id: string; label: string; start: string; end: string; documents: number; closedAt: number; closedBy: string }[];
}

/** Homeschool documents (#624). */
export type DocumentKind = 'notice' | 'acceptance' | 'ihip' | 'report' | 'evaluation' | 'test' | 'portfolio' | 'transcript' | 'diploma' | 'other';
export interface HomeschoolDocument {
  id: string;
  childId: string | null;
  title: string;
  kind: DocumentKind;
  date: string;
  source: 'upload' | 'made' | 'closeout';
  name: string;
  mime: string;
  size: number;
  createdAt: number;
}

/** A field trip (#622): everyone who went, the hours it counts for each, and this child's photos (work samples). */
export interface FieldTrip {
  id: string;
  date: string;
  place: string;
  learned: string;
  hours: number;
  subject: string;
  children: { id: string; name: string }[];
  photos: { id: string; name: string; mime: string }[];
}

/** Tests and evaluations (#612). */
export interface TestScore {
  subject: string;
  score: string;
  percentile: number | null;
}
export interface TestResult {
  id: string;
  kind: 'test' | 'evaluation';
  name: string;
  date: string;
  grade: string;
  givenBy: string;
  scores: TestScore[];
  note: string;
  file: { name: string; mime: string; size: number } | null;
}
export type ShareSection = 'summary' | 'attendance' | 'grades' | 'reading' | 'samples' | 'tests' | 'trips' | 'journal' | 'pdf';
/** A read-only evaluator link (#612). */
export interface EvaluatorLink {
  id: string;
  label: string;
  email: string;
  from: string;
  to: string;
  sections: ShareSection[];
  expiresAt: number;
  active: boolean;
  views: number;
  lastViewedAt: number | null;
  url: string;
}
/** What the evaluator's page shows (#612). */
export interface SharedYear {
  child: { name: string; grade: string };
  familyName: string;
  sharedBy: string;
  label: string;
  from: string;
  to: string;
  expiresAt: number;
  sections: ShareSection[];
  stats: { days: number; hours: number; books: number; samples: number };
  requirement: { state: string; option: string; days: number | null; hours: number | null; status: string | null } | null;
  subjects: { subject: string; hours: number }[];
  requiredSubjects: { name: string; subjects: string[]; hours: number; lessons: number }[];
  grades: { subject: string; pct: number; letter: string; count: number }[];
  reading: { date: string; title: string }[];
  journal: { date: string; html: string }[];
  samples: { id: string; date: string; name: string; caption: string; mime: string; subject: { subject: string; title: string } | null }[];
  tests: TestResult[];
  trips: { date: string; place: string; learned: string; hours: number; subject: string; went: string[] }[];
  pdf: boolean;
}
/** What a portfolio would hold (#603): the range, and a line per section (null when it doesn't apply). */
export interface PortfolioPreview {
  from: string;
  to: string;
  grade: string;
  sections: Record<PortfolioSection, string | null>;
  storage: { usedBytes: number; limitMb: number; maxMb: number };
}

/** A homeschool state requirement (#599): what one state's legal option asks for. */
export interface StateRequirement {
  id: string;
  state: string;
  stateName: string;
  option: string;
  days: number | null;
  hours: number | null;
  hoursPerDay: number | null;
  coreHours: number | null;
  /** Meeting the days OR the hours is enough. */
  either: boolean;
  perDay: 'minimum' | 'average';
  notes: string;
  sourceUrl: string;
  checkUrl: string;
  reviewedAt: string;
  /** Admin listing only: families following it. */
  families?: number;
  /** The family's own (umbrella school) target is used, where the state sets no number (#603). */
  custom?: boolean;
  /** Admin listing only: its filing deadlines (#609). */
  deadlines?: RuleDeadline[];
  /** The subjects it requires (#618). */
  requiredSubjects?: { name: string; aliases: string[] }[];
}

/** The high-school transcript (#620). */
export interface TranscriptCourse {
  id: string;
  grade: number;
  name: string;
  level: 'regular' | 'honors' | 'ap' | 'dual';
  credits: number;
  mark: string;
}
export interface TranscriptTotals {
  credits: number;
  gpa: number | null;
  weighted: number | null;
}
export interface Transcript {
  info: { ninth: number | null; graduation: string; guessed: boolean };
  settings: { honorsBonus: number; apBonus: number; defaultCredits: number };
  years: { grade: number; start: number | null; label: string; courses: TranscriptCourse[]; totals: TranscriptTotals }[];
  totals: TranscriptTotals;
}

/** A child's required-subjects checklist (#618). */
export interface RequiredChecklist {
  state: string;
  option: string;
  sourceUrl: string;
  items: { name: string; subjects: string[]; auto: boolean; hours: number; lessons: number }[];
  childSubjects: string[];
}

/** A filing deadline on a state rule (#609): 'MM-DD' each year, or 'start+N' / 'end-N' days from the school year. */
export interface RuleDeadline {
  id?: string;
  title: string;
  due: string;
  doc: '' | 'notice' | 'ihip' | 'report';
  familyDate: boolean;
}

/** One of a family's deadlines for a school year (#609). */
export interface StateDeadline extends Required<RuleDeadline> {
  year: number;
  date: string;
  /** The rule's date, when the family moved it. */
  ruleDate: string | null;
  filedAt: string | null;
  daysLeft: number;
}

/** Where the family files its state paperwork (#954): the official and the district. */
export interface Filing {
  officialName: string;
  officialTitle: string;
  district: string;
  districtAddress: string;
}
/** Who the state documents are from: the family's owner (primary parent), from their account (#954). */
export interface DocFrom {
  parentName: string;
  address: string;
  phone: string;
  email: string;
}
/** A child's details for state documents, from their profile, and what each kind of document still needs. */
export interface DocChild {
  id: string;
  name: string;
  legalName: string;
  birthDate: string;
  grade: string;
  curriculum: string;
  subjects: string[];
  missing: { notice: string[]; ihip: string[]; report: string[] };
}
/** State compliance for a child's family (#954): offered, turned on, and the child's details while it's on. */
export interface StateComplianceInfo {
  available: boolean;
  on: boolean;
  legalName: string;
  birthDate: string;
  curriculum: string;
}

/** A child's progress against the family's state requirement (#599). */
export type Compliance =
  | { requirement: StateRequirement; needsSchoolYear: true }
  | {
      requirement: StateRequirement;
      needsSchoolYear?: undefined;
      schoolYear: { start: string; end: string };
      days: number;
      shortDays: number;
      hours: number;
      hoursTarget: number | null;
      expectedDays: number | null;
      expectedHours: number | null;
      behindBy: number;
      schoolDaysLeft: number;
      estimateOnly: number;
      /** Core-subject hours (#607), where the state counts them (Missouri). */
      coreHours: number | null;
      coreTarget: number | null;
      status: 'met' | 'behind' | 'on-track' | 'not-started' | 'no-target';
    };

export interface Parent {
  id: string;
  /** When the account is due to be erased, if they asked (#518); 0 otherwise. */
  deleteAt?: number;
  /** The family they're working in, and every family they belong to (#506). Missing on older servers. */
  activeFamilyId?: string;
  families?: { id: string; name: string }[];
  email: string;
  name: string;
  timezone: string;
  /** Also has the platform admin portal (the server's ADMIN_EMAILS). */
  isAdmin?: boolean;
  /** Has the Brand Ambassador role: an ambassador's email is this account's (#721). */
  ambassador?: boolean;
  /** Wants focus alerts by email too. */
  emailAlerts?: boolean;
  /** The parent's color theme (#251); '' is the default look. */
  theme?: string;
  /** Per-category email notification preferences. */
  notifyPrefs?: NotifyPrefs;
  /** Per-category on-device (browser) notification preferences (#213). */
  browserPrefs?: NotifyPrefs;
  /** The server can send email (an admin set up SMTP). */
  emailAvailable?: boolean;
  /** Days website history is kept on this server. */
  historyDays?: number;
  /** "Also for" starts with every other child ticked when adding a site or app (#765; an admin setting). */
  alsoForAll?: boolean;
  /** Two-factor sign-in is set up on this account. */
  mfaEnabled?: boolean;
  /** The server requires two-factor for this account (set it up to use the dashboard). */
  mfaRequired?: boolean;
  /** The Terms changed since they accepted (or they never ticked the boxes): accept before carrying on (#589). */
  termsNeeded?: boolean;
  /** The current Terms version and when it was published (#589). */
  terms?: { version: number; updatedAt: number };
  /** Camera blur (#592): "Blur background" is offered on the parent's camera, and whether it starts on. */
  cameraBlur?: { offered: boolean; byDefault: boolean; forChildren?: boolean };
  /** Ghost mode (#928): whether parents may watch a child's screen without the banner. */
  ghostMode?: { allowed: boolean };
  /** A second factor is in place: the authenticator app, or a passkey where the admin counts those (#417). */
  mfaSatisfied?: boolean;
  /** Passkeys are on for this server, and how many this parent has (#417). */
  passkeys?: { enabled: boolean; count: number };
  /** This family may use the REST API / webhooks (an admin can turn it off per family). */
  apiEnabled?: boolean;
  /** The home family's subscription plan (#99). */
  plan?: FamilyPlan;
  /** The weekly summary email (#556): whether they get it, and when the server sends it (their time zone). */
  weeklySummary?: { on: boolean; available: boolean; day: number; hour: number };
  /** Billing (#540): payments are on, and whether the family is locked until it pays. */
  billing?: { enabled: boolean; locked: boolean; lockReason: string; status: string; graceEndsAt: number; /** A new family that has to pick its plan (#635). */ needsPlan?: boolean;
    /** For the family's owner (#871): the trial ending, a renewal coming, or a failed payment. */
    notice?: { kind: 'trial' | 'renewal' | 'price' | 'failed'; ref: string; date?: number; amountCents?: number; interval?: 'month' | 'year' } | null;
    /** After cancelling (#883): when the records will be deleted, or were (0 when neither). */
    records?: { deleteAt: number; deletedAt: number };
  };
}

/** GET /api/billing (#540): where the family stands and the plans it can subscribe to. */
export interface BillingInfo {
  enabled: boolean;
  needsPayment: boolean;
  /** A new family that has to pick its plan (#635). */
  needsPlan?: boolean;
  status: string;
  interval: string;
  periodEnd: number;
  cancelAtPeriodEnd: boolean;
  graceEndsAt: number;
  locked: boolean;
  lockReason: string;
  hasCustomer: boolean;
  /** Founders (#585): this family's lifetime discount in %, or 0. */
  founderPercent?: number;
  /** The partner who sent this family (#598), and the discount applied at checkout. */
  partner?: { code: string; name: string; discountPct: number; discountMonths: number } | null;
  /** Give a month, get a month (#554): the owner's link and what it has earned; null when off. */
  referral?: { code: string; link: string; signedUp: number; rewarded: number; creditCents: number; pending: number } | null;
  /** Paused for summer until then (ms), or 0 (#555); and how many months a pause can be now (0 = not now). */
  pausedUntil?: number;
  canPauseMonths?: number;
  isOwner: boolean;
  ownerName: string;
  taxEnabled: boolean;
  plans: {
    id: string;
    name: string;
    description: string;
    monthlyCents: number;
    yearlyCents: number;
    yearlyDiscountPct: number;
    current: boolean;
    /** Free days a new family gets when it picks this plan (#635). */
    trialDays?: number;
    /** Plans by family size (#633): who it covers (null = no limit), the plan it's the bigger version of, and its per-child add-on. */
    maxAdults?: number | null;
    maxChildren?: number | null;
    largerOf?: string;
    childAddon?: { id: string; amount: number; monthlyCents: number; yearlyCents: number; maxQuantity: number } | null;
  }[];
  /** Paid extras the family can buy on its plan (#546), with how many it has. */
  addons?: { id: string; name: string; kind: 'adults' | 'children' | 'feature'; amount: number; features: string[]; monthlyCents: number; yearlyCents: number; quantity: number }[];
}

/** Admin → Server settings → Payments (#540). */
export interface BillingAdmin {
  enabled: boolean;
  graceDays: number;
  taxEnabled: boolean;
  /** Days a cancelled family's records are kept after access ends (#883), and whether ended trials count. */
  recordsKeepDays?: number;
  recordsAfterTrials?: boolean;
  /** Checkout asks families to agree to the renewal terms (#881). */
  checkoutConsent?: boolean;
  connected: boolean;
  mode: 'test' | 'live' | '';
  accountName: string;
  accountId: string;
  webhookUrl: string;
  webhookSet: boolean;
  /** Repair webhook (#587): the endpoint in use, and what it last received. */
  webhookEndpointId?: string;
  lastEventAt?: number;
  lastEventType?: string;
  lastBadSignatureAt?: number;
  connectedAt: number;
  /** Founders (#585). */
  foundersEnabled?: boolean;
  foundersPercent?: number;
  founderFamilies?: number;
  /** Referrals (#554). */
  referralsEnabled?: boolean;
  referralMaxPerYear?: number;
  /** Pause for summer (#555). */
  pauseEnabled?: boolean;
  pauseMaxMonths?: number;
  pauseMonths?: number[];
  /** New families pick their plan first (#635). */
  newFamiliesChoosePlan?: boolean;
}

export interface Policy {
  childId: string;
  mode: 'auto' | 'on' | 'off';
  schoolTime: boolean;
  /** Inside the child's yearly pause (like summer break). */
  paused?: boolean;
  /** The pause's last day ('MM-DD') while paused. */
  pauseEnd?: string;
  /** Today is a no-school day from the family calendar (a holiday/break or outside the school year). */
  noSchoolDay?: boolean;
  /** The holiday/break name when it's a no-school day. */
  holidayName?: string;
  /** The parent ended school early today after the child said they were done (#440); back on schedule tomorrow. */
  doneForDay?: boolean;
  /** What the child's School Resources show (#454); missing on older servers (show everything). */
  features?: string[];
  onBreak: boolean;
  breakUntil: number;
  monitoring: boolean;
  /** Ghost mode (#928): watch this child's screen without the watching banner (camera and mic still show it). */
  ghostMode?: boolean;
  nextChange: number | null;
  blockedSites: string[];
  learningSites: string[];
  /** During school time, everything except the learning sites is blocked (#175). */
  learningOnly?: boolean;
  /** Games are blocked during school time (#290): game websites are in blockedSites, and the apps the agents close. */
  blockGames?: boolean;
  blockedApps?: { name: string }[];
  /** Camera blur (#592): the child may blur their camera background, and whether it's on before they choose. */
  blurAllowed?: boolean;
  blurDefault?: boolean;
  serverTime: number;
}

export interface DeviceStatus {
  screen: boolean;
  camera: boolean;
  mic: boolean;
  audio: boolean;
  viewers: number;
  /** Can be controlled with Interact (#351): false when a Mac lacks the Accessibility permission. */
  control?: boolean;
  /** The child blurred their camera background (#592). */
  blurred?: boolean;
}

export interface Device {
  id: string;
  childId: string;
  name: string;
  kind: 'agent' | 'extension';
  platform: string;
  appVersion?: string;
  /** Shared by the family's children (#442); childId is whoever is using it now. */
  shared?: boolean;
  /** An extension: the computer it's installed on (#675), when that computer's app set it up; '' otherwise. */
  viaDeviceId?: string;
  createdAt: number;
  lastSeenAt: number;
  /** A parent asked to remove the app from this computer (#572); it happens when the computer is next online. 0 = not asked. */
  uninstallRequestedAt?: number;
  online?: boolean;
  status?: DeviceStatus;
}

/** A child's devices for showing (#675): each computer with the browser extensions its app set up, then any others. */
export function groupDevices(devices: Device[]): { device: Device; extensions: Device[] }[] {
  const computers = new Set(devices.filter((d) => d.kind === 'agent').map((d) => d.id));
  const under = (d: Device) => d.kind === 'extension' && !!d.viaDeviceId && computers.has(d.viaDeviceId);
  return devices.filter((d) => !under(d)).map((d) => ({ device: d, extensions: d.kind === 'agent' ? devices.filter((e) => under(e) && e.viaDeviceId === d.id) : [] }));
}

export interface UpdateStatus {
  phase: 'checking' | 'downloading' | 'installing' | 'uptodate' | 'error';
  percent: number;
  version?: string;
  message?: string;
}

export interface Child {
  id: string;
  /** Its family (#652). Missing on older servers. */
  familyId?: string;
  name: string;
  color: string;
  mode: Policy['mode'];
  /** Optional profile: age (null when unset) and grade label. */
  age?: number | null;
  grade?: string;
  /** The subjects the parent picked for this child (#247); they fill the Subject fields. */
  subjects?: string[];
  policy: Policy;
  devices: Device[];
  /** Student Portal (#136): whether the child can sign in, and the code to give them. */
  studentPortal?: { enabled: boolean; code: string | null; locked?: boolean; cooldownUntil?: number };
  /** Whether an external planner feed is linked for this child. */
  plannerLinked?: boolean;
  /** Which provider the linked feed is (connexus/google/canvas/apple/outlook/other); '' when none. */
  plannerSource?: string;
  /** The child's family's school year ('YYYY-MM-DD'), or null when it isn't set in Settings (#183). */
  schoolYear?: { start: string; end: string } | null;
  /** Progress against the family's state requirement (#599); null when not tracking. */
  compliance?: Compliance | null;
  /** State compliance (#954): whether it's offered and on, and the child's details for state paperwork. */
  stateCompliance?: StateComplianceInfo;
  /** During school time, only the learning sites are allowed (#175). */
  learningOnly?: boolean;
  /** Game websites and apps are blocked during school time (#290). */
  blockGames?: boolean;
  /** The parent lets this child blur their camera background (#592). */
  blurAllowed?: boolean;
  /** Ghost mode (#928): watch this child's screen without the watching banner. */
  ghostMode?: boolean;
  /** Which of the child's subjects count as core (#607). */
  coreSubjects?: string[];
  /** The family's grading scale (#300), highest letter first. */
  gradingScale?: GradeBand[];
  /** Turning in work needs "What I learned today" (#338). */
  requireLearned?: boolean;
  /** Hidden mode (#829): School Resources hidden on the child's computers, and whether the admin offers it. */
  agentHidden?: boolean;
  agentHideAllowed?: boolean;
  /** Categories of apps and websites blocked during school time, and single blocked apps (#334). */
  blockCategories?: string[];
  blockedApps?: { name: string; process: string; platform: 'windows' | 'mac' }[];
  /** Learning apps (#443): never closed, and their time counts toward the attendance estimate. */
  learningApps?: { name: string; process: string; platform: 'windows' | 'mac' }[];
  /** What the child's family plan includes (#99); missing on older servers (treat as everything). */
  features?: FeatureKey[];
  /** Features the parent hid for this child (#454), and what the child actually sees. */
  hiddenFeatures?: string[];
  visibleFeatures?: string[];
  /** Turned off for the whole server by an admin (#469). */
  offFeatures?: string[];
  /** What you may do with this child, per area (#471); missing on older servers (everything). */
  myAccess?: Record<string, AccessLevel>;
}

/** The family-screen inbox (#169): unread chat and "done for today" requests per child. */
export interface Inbox {
  chats: { childId: string; childName: string; childColor: string; count: number; lastText: string; lastTs: number }[];
  /** "I'm done for today" waiting for an answer (#440). Missing on older servers. */
  doneRequests?: { childId: string; childName: string; childColor: string; activityId: number; ts: number; via: 'portal' | 'device' }[];
  /** Whether any child shows Chat / Requests (#504); Home hides a tile when none does. Missing on older servers. */
  shown?: { chat: boolean };
}

export interface Activity {
  id: number;
  childId: string;
  deviceId: string | null;
  ts: number;
  kind: string;
  title: string;
  detail: string;
}

/** An activity item from the cross-child dashboard feed, tagged with who it belongs to (#154). */
export interface FeedActivity extends Activity {
  childName: string;
  childColor: string;
}

export interface ScheduleBlock {
  day: number;
  start: number;
  end: number;
}

/** The family school schedule (#248) and which children are on it. */
export interface FamilySchedule {
  /** False until the parent saves one; `blocks` is then a weekday starting point. */
  set: boolean;
  blocks: ScheduleBlock[];
  children: { id: string; name: string; color: string; matches: boolean }[];
}

/** How the laser pointer looks on the child's screen (the server accepts only these choices). */
export interface PointerStyle {
  color: '#ef4444' | '#f59e0b' | '#22c55e' | '#3b82f6' | '#a855f7';
  size: 'small' | 'medium' | 'large';
  shape: 'dot' | 'ring' | 'arrow';
  /** Keep showing where it was left, instead of fading out when the parent stops moving it. */
  stay: boolean;
}

/** A pause in school time every year, like summer break: 'MM-DD' to 'MM-DD', both days included. */
export interface YearlyPause {
  start: string;
  end: string;
}

export interface SiteRule {
  id: string;
  kind: 'block' | 'learn';
  pattern: string;
}

// --- Platform admin portal ----------------------------------------------------

export interface AdminDevice {
  id: string;
  name: string;
  kind: 'agent' | 'extension';
  platform: string;
  appVersion: string;
  online: boolean;
  lastSeenAt: number;
}

export interface AdminChild {
  id: string;
  name: string;
  createdAt: number;
  devices: AdminDevice[];
  /** The Student Portal is locked after failed sign-ins (#393), or cooling down. */
  studentLocked?: boolean;
  studentCooldown?: boolean;
}

export interface AdminParent {
  id: string;
  /** When the account is due to be erased, if they asked (#518); 0 otherwise. */
  deleteAt?: number;
  name: string;
  email: string;
  timezone: string;
  createdAt: number;
  /** When they last ticked both boxes (ms), or 0; which Terms version, and from which IP (#589). */
  termsAcceptedAt: number;
  termsVersion?: number;
  termsIp?: string;
  /** Suspended by an admin: can't sign in. */
  disabled: boolean;
  isAdmin: boolean;
  /** A Brand Ambassador's email (#721). */
  ambassador?: boolean;
  /** An ADMIN_EMAILS admin: can't be suspended or deleted from the portal. */
  locked: boolean;
  /** Two-factor set up on this account. */
  mfaEnabled: boolean;
  /** This account is specifically required to use two-factor by an admin. */
  mfaRequired: boolean;
  /** This parent's email notification preferences, which the admin can manage (#165). */
  notifyPrefs: NotifyPrefs;
  children: AdminChild[];
  /** Failed sign-in protection (#393). */
  signin?: { failed: number; cooldownUntil: number; locked: boolean; lastFailedAt: number; lastFailedIp: string };
  /** How many times they've signed in, and the last time (#429). */
  signIns?: { count: number; lastAt: number };
}

export interface AdminFamilyMember {
  id: string;
  name: string;
  email: string;
  role: 'owner' | 'member' | string;
  isAdmin: boolean;
  disabled: boolean;
  mfaEnabled: boolean;
  /** What they can do (null for the owner) and which students they see (#514). Missing on older servers. */
  permissions?: MemberPermissions | null;
  childIds?: string[];
}

export interface AdminFamily {
  id: string;
  name: string;
  createdAt: number;
  members: AdminFamilyMember[];
  invites: { email: string; expiresAt: number }[];
  children: AdminChild[];
  /** The family's plan and add-ons (#99). */
  plan?: AdminFamilyPlan;
  /** The ambassador who sent it and whether their discount is on its subscription (#877). */
  ambassador?: FamilyAmbassador | null;
  /** The sign-up code it used and whether its discount is on (#879). */
  signupCode?: FamilySignupCode | null;
  /** After cancelling (#883): when its records will be deleted, or were. */
  records?: { deleteAt: number; deletedAt: number };
}

export interface FamilySignupCode {
  code: string;
  note: string;
  founder: boolean;
  percentOff: number;
  months: number;
  usedAt: number;
  status: 'applied' | 'used' | 'not-subscribed' | 'not-applied';
  endsAt: number;
}

export interface FamilyAmbassador {
  partnerId: string;
  name: string;
  code: string;
  kind: string;
  joinedAt: number;
  firstPaidAt: number;
  discountPct: number;
  discountMonths: number;
  status: 'applied' | 'used' | 'not-subscribed' | 'founder' | 'not-applied';
  endsAt: number;
  earnedCents: number;
}

/** Subscription plans (#99). */
export type FeatureKey =
  /** The app or extension on the child's computer (#564). */
  | 'computer'
  | 'live_view'
  | 'interact'
  | 'whiteboard'
  | 'chat'
  | 'lesson_plans'
  | 'journal'
  | 'grade_book'
  | 'report_cards'
  | 'portfolio'
  | 'attendance'
  | 'state_compliance'
  | 'reading_log'
  | 'reports'
  | 'app_blocking'
  | 'student_portal'
  | 'todo'
  | 'api';
export interface FeatureInfo {
  key: FeatureKey;
  label: string;
  hint: string;
}
export interface Plan {
  id: string;
  name: string;
  description: string;
  priceMonthlyCents: number;
  yearlyDiscountPct: number;
  /** An exact yearly price; 0 = 12 months less the discount. */
  priceYearlyCents?: number;
  /** null = unlimited. */
  maxAdults: number | null;
  maxChildren: number | null;
  features: FeatureKey[];
  addonIds: string[];
  earlyAccess: boolean;
  freeForLife: boolean;
  /** Ambassador access (#895): no charge while the family's Brand Ambassador is active. */
  ambassadorAccess?: boolean;
  trialDays: number;
  freeFrom: string;
  freeUntil: string;
  signupStart: string;
  signupEnd: string;
  isDefault: boolean;
  /** Highlighted in the home page's plans (#710). */
  recommended: boolean;
  archived: boolean;
  sort: number;
  /** How many families are on it (admin list only). */
  families?: number;
  /** The plan this is the bigger (Large Family) version of, or '' (#633). */
  largerOf?: string;
}
export interface Addon {
  id: string;
  name: string;
  kind: 'adults' | 'children' | 'feature';
  amount: number;
  /** The first of `features` (older servers send only this). */
  feature: FeatureKey | '';
  /** What a feature add-on unlocks (#394). */
  features?: FeatureKey[];
  /** The plans it can be used with; empty = every plan (#394). */
  planIds?: string[];
  /** An exact yearly price; 0 = 12 months less the plan's discount. */
  priceYearlyCents?: number;
  priceMonthlyCents: number;
  /** The most a family can buy; 0 = no limit (#633). */
  maxQuantity?: number;
  archived: boolean;
}
export type PlanStatus = 'unrestricted' | 'free-for-life' | 'trial' | 'free' | 'paid';
export interface FamilyAddon {
  id: string;
  name: string;
  kind: string;
  amount: number;
  feature: string;
  features?: string[];
  quantity: number;
  fromPlan: boolean;
}
export interface FamilyPlan {
  plan: Plan | null;
  features: FeatureKey[];
  maxAdults: number | null;
  maxChildren: number | null;
  status: PlanStatus;
  freeUntil: string;
  addons: FamilyAddon[];
  counts: { adults: number; invites: number; children: number };
  catalog: FeatureInfo[];
}
export interface AdminFamilyPlan {
  planId: string;
  planName: string;
  /** Marked a Founder (#585). */
  founder?: boolean;
  /** When the owner agreed to the renewal terms at checkout, and the words (#881). */
  renewalConsentAt?: number;
  renewalConsentText?: string;
  status: PlanStatus;
  freeUntil: string;
  maxAdults: number | null;
  maxChildren: number | null;
  addons: FamilyAddon[];
  /** Expected monthly amount in cents (#384): what it pays now (0 while free), and once any free period ends. */
  monthlyCents?: number;
  regularMonthlyCents?: number;
}

export interface AdminOverview {
  totals: { parents: number; children: number; devices: number; onlineDevices: number; families: number };
  parents: AdminParent[];
  families: AdminFamily[];
  /** The Can do roles, areas and levels (#514). */
  permissions?: PermissionCatalog;
}

export interface ApiFamily {
  id: string;
  name: string;
  apiEnabled: boolean;
  ownerName: string;
  ownerEmail: string;
  keyCount: number;
  webhookCount: number;
}

export interface ApiKeySummary {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  createdAt: number;
  lastUsedAt: number | null;
  expiresAt: number | null;
  ownerName: string;
  ownerEmail: string;
}

export interface ApiWebhookSummary {
  id: string;
  url: string;
  events: string[];
  disabled: boolean;
  createdAt: number;
  lastStatus: number | null;
  lastAt: number | null;
  ownerName: string;
  ownerEmail: string;
}

export interface ApiFamilyConfig {
  id: string;
  name: string;
  apiEnabled: boolean;
  keys: ApiKeySummary[];
  webhooks: ApiWebhookSummary[];
}

export interface AdminAccessRequest {
  id: string;
  name: string;
  email: string;
  note: string;
  status: 'pending' | 'approved' | 'declined';
  createdAt: number;
  decidedAt: number | null;
  decidedBy: string;
  reason: string;
}

export interface IceServer {
  urls: string | string[];
  username?: string;
  credential?: string;
}

export interface BootSettings {
  host: string;
  port: number;
  trustProxy: boolean;
}

/** What the admin portal's Server settings show. Secrets are only reported as set / not set. */
/** Failed sign-in protection (#393). */
export interface LockoutPolicy {
  enabled: boolean;
  cooldownAfter: number;
  cooldownMinutes: number;
  lockAfter: number;
  students: boolean;
  notifyOwner: boolean;
  notifyAdmins: boolean;
}

export interface PlatformSettings {
  /** Failed sign-in protection (#393). */
  lockout: LockoutPolicy;
  /** Passkeys (#417): Server settings → Sign-in security. */
  passkeys: PasskeyPolicy;
  /** Help & Feedback (#414): Server settings → Support. */
  support: SupportPolicy;
  /** The theme for accounts that haven't picked one (#360); '' is Default · Match device. */
  defaultTheme: string;
  /** The School Resources theme for children who haven't picked one of its nine (#819); missing on older servers. */
  agentDefaultTheme?: string;
  /** Address used in emailed links; empty means "whatever address the request came in on". */
  publicUrl: string;
  registrationOpen: boolean;
  /** School site readers (#654): the extension reads planners and grades from supported school sites. */
  schoolReaders?: boolean;
  /** Parents can send Connexus's own attendance from their browser with a bookmark (#680). */
  connexusBookmark?: boolean;
  /** The ClassGuardian app can open the Student Portal already signed in (#684). */
  studentPortalLinks?: boolean;
  /** Alert parents when a child leaves the learning sites (#847); off by default. */
  offTaskAlerts?: boolean;
  /** How often the child's computer reads its school site in the background, in hours (#673); 0 = never. */
  schoolReadHours?: number;
  /** Agreeing to the Terms (#589): the current version, whether existing parents must accept it, and how many have. */
  legal?: { version: number; updatedAt: number; reaccept: boolean; accepted: number; parents: number };
  /** An admin's custom CSS per area (#367): every page, landing, sign-in, agent page, student portal. */
  customCss?: Partial<Record<'all' | 'landing' | 'login' | 'agent' | 'student', string>>;
  requireMfa: 'off' | 'admins' | 'everyone';
  /** From ADMIN_EMAILS; can't be removed in the portal. */
  envAdminEmails: string[];
  /** Added in the portal. */
  adminEmails: string[];
  /** Which platform/system events email the admins (#165). */
  platformNotifications: { signups: boolean; feedback: boolean };
  offlineGraceSeconds: number;
  retentionDays: number;
  alsoForAll?: boolean;
  /** How long Audit log → Sign-ins keeps each sign-in (#429). */
  signInHistoryDays?: number;
  accountDeletionDays?: number;
  /** How often agents check for an update, in hours (#550). */
  agentUpdateHours?: number;
  /** Seconds before the agents' School Resources button folds to the logo (#569); 0 = never. */
  launcherCollapseSeconds?: number;
  /** Camera blur (#592): offered to parents, offered to children, and on before people choose. */
  cameraBlur?: { parent: boolean; child: boolean; byDefault: boolean };
  ghostMode?: { allowed: boolean };
  /** Hidden mode (#829): parents may hide School Resources, and new children start hidden. */
  agentHiding?: { allowed: boolean; byDefault: boolean };
  /** Work samples and the portfolio (#603). */
  portfolio?: { enabled: boolean; studentUploads: boolean; maxMb: number; familyStorageMb: number; retentionYears: number };
  /** The weekly summary email (#556). */
  weeklySummary?: { enabled: boolean; day: number; hour: number; defaultOn: boolean };
  /** The first-run checklist on Home (#552). */
  onboarding?: { enabled: boolean; steps: string[] };
  webhookAllowPrivate: boolean;
  /** Children can say they're done for the day (#440). */
  /** Features new children start with hidden (#454). */
  defaultHiddenFeatures?: string[];
  /** Features turned off for everyone (#469). */
  disabledFeatures?: string[];
  /** Billing with Stripe (#540). */
  billing?: BillingAdmin;
  stunUrls: string[];
  turn: { urls: string[]; username: string; credentialSet: boolean };
  smtp: {
    host: string;
    port: number;
    security: 'tls' | 'starttls' | 'none';
    username: string;
    passwordSet: boolean;
    from: string;
    configured: boolean;
  };
  github: { repo: string; tokenSet: boolean; appId: string; privateKeySet: boolean; mode: 'app' | 'token' | 'none'; configured: boolean };
  /** True when ENCRYPTION_KEY is set, so stored secrets (TOTP seeds, SMTP/TURN/GitHub creds) are encrypted. */
  encryptedAtRest: boolean;
  extensionStore: { chromeId: string; chromeUpdateUrl: string; edgeId: string; edgeUpdateUrl: string };
  /** Applied on the next start. */
  boot: BootSettings;
  /** What the server is running with now. */
  running: BootSettings;
  restartNeeded: boolean;
  fixed: { dataDir: string; puid: string; pgid: string; supervised: boolean };
}

/** How someone the family is shared with is related to it (#363). A label; it doesn't change what they can do. */
export type Relationship = 'parent' | 'guardian' | 'tutor' | 'caregiver';

/** Co-parent permissions (#471). */
export type AccessLevel = 'none' | 'view' | 'edit' | 'full';
/** A role id: Co-parent, Tutor, Viewer, one an admin added (#516), or 'custom'. */
export type PermissionRole = string;
export interface MemberPermissions {
  role: PermissionRole;
  levels: Record<string, AccessLevel>;
}
/** The permission pickers' choices, from GET /api/family (server/src/permissions.ts is the one source). */
export interface PermissionCatalog {
  areas: { key: string; label: string; hint: string }[];
  roles: { value: PermissionRole; label: string; hint: string }[];
  levels: { value: AccessLevel; label: string }[];
}
export interface FamilyMember {
  id: string;
  name: string;
  email: string;
  role: 'owner' | 'member';
  relationship?: Relationship;
  isYou: boolean;
  /** The ids of the family's students this member may see (owners always see all). */
  childIds: string[];
  /** What they may do (#471); null for the owner. Missing on older servers. */
  permissions?: MemberPermissions | null;
}

export interface FamilyGroup {
  id: string;
  name: string;
  /** This is the parent's own home family (where their children live). */
  isHome: boolean;
  /** The family's students, so the owner can choose which ones each co-parent sees. */
  children: { id: string; name: string }[];
  members: FamilyMember[];
  invites: { email: string; expiresAt: number; relationship?: Relationship; role?: PermissionRole }[];
}

/** Passkeys (#417). */
export interface PasskeyPolicy {
  enabled: boolean;
  countAsMfa: boolean;
}

/** Help & Feedback tickets (#414). */
export type TicketType = 'question' | 'problem' | 'idea' | 'account';
/** Server settings → Support (#414 phase 2). */
export interface SupportPolicy {
  types: TicketType[];
  /** How to reach a person (#529). */
  contactNote?: string;
  contactEmail?: string;
  contactPhone?: string;
  autoCloseDays: number;
  openPerHour: number;
  emailAdminsOnReply: boolean;
  emailFamilyOnReply: boolean;
  labels: Record<TicketType, string>;
  /** A label every escalated issue also gets (#576); '' for none. */
  escalationLabel?: string;
  /** Phase 3 (#446). Missing on older servers. */
  attachmentsEnabled?: boolean;
  attachmentMaxMb?: number;
  attachmentsPerMessage?: number;
  allowPdf?: boolean;
  githubSync?: boolean;
  fixedReply?: string;
  savedReplies?: SavedReply[];
}
/** Admin → Support's report tiles. */
export interface SupportReport {
  open: number;
  openedLast30Days: number;
  resolvedLast30Days: number;
  answeredLast30Days: number;
  avgFirstReplyMs: number | null;
  openByType: Record<TicketType, number>;
}
export type TicketStatus = 'open' | 'in_progress' | 'waiting' | 'resolved' | 'closed';
/** A screenshot or file on a request (#446). */
export interface TicketAttachment {
  id: string;
  name: string;
  mime: string;
  size: number;
}
/** What the server allows for attachments (#446). */
export interface AttachmentPolicy {
  enabled: boolean;
  maxMb: number;
  perMessage: number;
  pdf: boolean;
}
export interface SavedReply {
  id: string;
  title: string;
  body: string;
}
export interface TicketMessage {
  id: string;
  author: { kind: 'parent' | 'student' | 'admin' | 'system'; name: string };
  body: string;
  /** A private note: admins only. */
  internal: boolean;
  createdAt: number;
  attachments?: TicketAttachment[];
}
export interface TicketSummary {
  id: string;
  number: number;
  type: TicketType;
  subject: string;
  status: TicketStatus;
  openedBy: { kind: 'parent' | 'student' | 'admin'; name: string };
  /** Escalated to a GitHub issue ("With the developers"). */
  issueNumber: number | null;
  /** Support replied since you last opened it (#414 phase 2). */
  unread?: boolean;
  createdAt: number;
  updatedAt: number;
}
export interface TicketDetail extends TicketSummary {
  messages: TicketMessage[];
}
/** A ticket as Admin → Support sees it. */
export interface AdminTicket extends TicketSummary {
  openedBy: { kind: 'parent' | 'student' | 'admin'; name: string; email: string; parentId: string; childName: string };
  familyName: string;
  priority: 'low' | 'normal' | 'high';
  assignee: string;
  issueUrl: string;
  resolvedAt: number | null;
  /** The family spoke last: it needs an answer. */
  needsAnswer?: boolean;
}
export interface AdminTicketDetail extends AdminTicket {
  messages: TicketMessage[];
}

export interface ChildReport {
  childName: string;
  childAge: number | null;
  childGrade: string;
  from: number;
  to: number;
  timezone: string;
  siteVisits: number;
  activeMinutes: number;
  daysActive: number;
  /** Most time first (#861); `status` says whether it's a learning site or blocked now. */
  topSites: { host: string; count: number; minutes: number; status: 'learning' | 'blocked' | 'other' }[];
  bySite: { host: string; visits: number; minutes: number }[];
  /** Minutes in each learning app (#443); missing on older servers. */
  byApp?: { name: string; minutes: number }[];
  blocked: number;
  offTask: number;
  offline: number;
  breaks: number;
  byDay: { date: string; visits: number; blocked: number; offTask: number; activeMinutes: number }[];
}

export type ReportMetric = 'active' | 'visits' | 'blocked' | 'offtask';

export interface ReportItems {
  metric: ReportMetric;
  timezone: string;
  count: number;
  items: { ts: number | null; primary: string; secondary: string }[];
}

export interface Attendance {
  timezone: string;
  from: number;
  to: number;
  schoolDays: number;
  presentDays: number;
  holidayDays: number;
  days: { date: string; weekday: string; present: boolean; visits: number; firstSeen: number | null; lastSeen: number | null }[];
}

/** The attendance hours the parent recorded over a range (#250). */
export interface RecordedAttendance {
  timezone: string;
  from: string;
  to: string;
  days: { date: string; weekday: string; hours: number }[];
  daysAttended: number;
  totalHours: number;
  averageHours: number;
}

export interface Assignment {
  id: string;
  subject: string;
  name: string;
  pointsEarned: number;
  pointsAvailable: number;
  weight: number;
  createdAt: number;
  /** When it was done, 'YYYY-MM-DD' (#254). */
  date: string;
  /** Homework, Quiz, Test… (#307), or ''. */
  category?: string;
  /** Added from a planner item marked "is graded" (#184). 0 points available = not scored yet. */
  fromPlanner?: boolean;
  /** Read from the child's school site (#654), e.g. 'connexus': it changes there, so it isn't edited here. */
  fromSchool?: string;
  /** A school-site grade's type there (e.g. 'Skills Check') and that type's weight in its course, to grade it the school's way (#821). */
  schoolType?: string;
  typeWeight?: number;
  /** That planner item, whose work samples it shares (#637). */
  plannerItemId?: string | null;
  /** How many work samples it has (#637). */
  samples?: number;
}

export interface PlannerItem {
  id: string;
  title: string;
  description: string;
  subject: string;
  /** Homework, Quiz, Test… (#307), or ''. */
  category?: string;
  url: string;
  dueDate: string;
  done: boolean;
  createdAt: number;
  /** '' = added by the parent; otherwise the provider key of the linked feed it was imported from. */
  source?: string;
  /** "Is graded": it's also in the grade book (#184). */
  isGraded?: boolean;
  /** Turned in from the Student Portal (#188): when (ms), what they learned, and their sticker. */
  submittedAt?: number | null;
  studentNote?: string;
  sticker?: string;
  /** The lesson plan it goes with (#320), or ''. */
  lessonPlanId?: string;
  /** Set when it was made as one of a repeating series (#324). */
  seriesId?: string;
  /** A scheduled time on the due date (#366), 'HH:MM' 24-hour, or '' for any time that day. */
  startTime?: string;
  endTime?: string;
  /** A school site's scheduled class, e.g. a Connexus LiveLesson (#693): attended, not turned in. */
  event?: boolean;
}

/** A parent's daily lesson plan for a child (#186). The *Html fields are sanitized rich text. */
export interface LessonPlan {
  id: string;
  date: string;
  subject: string;
  topic: string;
  focusHtml: string;
  materials: { text: string; done: boolean }[];
  objectives: string[];
  activityHtml: string;
  assessmentHtml: string;
  createdAt: number;
  updatedAt: number;
}

/** A parent's journal entry about a child's day (#187). bodyHtml is sanitized rich text. */
export interface JournalEntry {
  date: string;
  bodyHtml: string;
  author: string;
  createdAt: number;
  updatedAt: number;
}

/** One day's reading (#189), logged by the student and signed off by a parent. */
/** A task on the parents' to-do list (#332). */
export interface ParentTask {
  id: string;
  title: string;
  /** 'YYYY-MM-DD', or '' with no due date. */
  dueDate: string;
  doneAt: number | null;
  doneBy: string;
  createdBy: string;
  createdAt: number;
}

export interface ReadingEntry {
  id: string;
  date: string;
  title: string;
  pages: number;
  minutes: number;
  signedOff: boolean;
  signedOffAt: number | null;
  signedOffBy: string;
  createdAt: number;
  /** The parent who added it (#339), or '' when the student did. */
  addedBy?: string;
}

export interface FamilyCalendar {
  schoolYearStart: string;
  schoolYearEnd: string;
  breaks: { id: string; name: string; start: string; end: string }[];
}

export interface StandardHoliday {
  name: string;
  start: string;
  end: string;
  /** Already on the family's calendar (#462). */
  added?: boolean;
}

export interface AuditEntry {
  id: number;
  ts: number;
  adminEmail: string;
  action: string;
  detail: string;
  /** In plain words (#423): who did it (by name), what happened, and any detail lines. */
  who: string;
  title: string;
  lines: string[];
}

/** Admin → Audit log → Sign-ins (#429). */
export interface SignInEntry {
  id: number;
  ts: number;
  kind: 'parent' | 'student';
  subjectId: string;
  name: string;
  login: string;
  result: 'ok' | 'failed' | 'blocked';
  method: string;
  reason: string;
  ip: string;
  device: string;
}
export interface SignInLog {
  entries: SignInEntry[];
  people: { id: string; label: string; kind: string }[];
  summary: { total: number; ok: number; unsuccessful: number; people: number };
  keepDays: number;
}

export interface AuditLog {
  entries: AuditEntry[];
  /** Distinct admins and actions across the whole log, for the filter dropdowns. */
  admins: { id: string; label: string }[];
  actions: { id: string; label: string }[];
}

/** A letter grade and the lowest percentage that earns it (#300). */
export interface GradeBand {
  letter: string;
  min: number;
}

export const DEFAULT_SCALE: GradeBand[] = [
  { letter: 'A', min: 90 },
  { letter: 'B', min: 80 },
  { letter: 'C', min: 70 },
  { letter: 'D', min: 60 },
  { letter: 'F', min: 0 },
];

export const letterFor = (scale: GradeBand[] | undefined, pct: number) => {
  const s = scale?.length ? scale : DEFAULT_SCALE;
  return (s.find((b) => pct >= b.min) ?? s[s.length - 1]!).letter;
};

/** Assignment categories (#307), alphabetical; must match the server's ASSIGNMENT_CATEGORIES. */
export const ASSIGNMENT_CATEGORIES = ['Activity', 'Coursework', 'Final Exam', 'Homework', 'Lab', 'Portfolio', 'Practice', 'Quiz', 'Test', 'Unit Test'] as const;

/** Admin → Finances (#398). */
export type FinanceFrequency = 'daily' | 'monthly' | 'yearly';
export interface FinanceExpense {
  id: string;
  date: string;
  amountCents: number;
  memo: string;
  /** Saved from a Brand Ambassador payout (#727): changed only through the payout. */
  fromPayout?: boolean;
}
export interface FinanceCost {
  id: string;
  name: string;
  amountCents: number;
  frequency: FinanceFrequency;
  startDate: string;
  dueDate: string;
  endDate: string;
  /** What it comes to in the month shown. */
  monthCents: number;
  nextDue: string;
}
export interface FinanceReport {
  month: string;
  /** Real subscriptions from Stripe (#540). */
  billing?: { mrrCents: number; active: number; trialing: number; pastDue: number; cancelling: number; ended: number };
  incomeCents: number;
  regularIncomeCents: number;
  payingFamilies: number;
  expensesCents: number;
  operatingCents: number;
  netCents: number;
  expenses: FinanceExpense[];
  costs: FinanceCost[];
  /** Brand Ambassador money (#723): paid out this month (off the net), earned this month, owed and waiting now. */
  ambassadors?: {
    paidCents: number;
    earnedCents: number;
    owedCents: number;
    waitingCents: number;
    payouts: { paidAt: number; name: string; amountCents: number; method: string }[];
    /** Paid this year outside Stripe (#725), for the accountant's 1099s. */
    outsideStripe?: { name: string; amountCents: number; methods: string }[];
    year?: number;
  };
  /** The business's time zone for the books (#745). */
  timeZone?: string;
}
